import React, { useState, useEffect } from 'react';
import api from '../../api';
import './TutorialsTab.css';

export default function TutorialsTab({ user }) {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    youtubeUrl: '',
    categories: [],
    description: ''
  });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const isCoach = user?.role === 'coach' || user?.role === 'headcoach';

  // Fetch videos
  useEffect(() => {
    loadVideos();
    loadCategories();
  }, [searchTerm, selectedCategory]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);

      const response = await api.get(`/videos?${params}`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/videos/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadForm.title || !uploadForm.youtubeUrl) {
      alert('Title and YouTube URL required');
      return;
    }

    try {
      setUploadLoading(true);
      await api.post('/videos', uploadForm);
      setUploadForm({ title: '', youtubeUrl: '', categories: [], description: '' });
      setShowUploadForm(false);
      loadVideos();
      alert('Video uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload video');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleEdit = async (videoId) => {
    try {
      await api.patch(`/videos/${videoId}`, editForm);
      setEditingId(null);
      loadVideos();
      alert('Video updated successfully!');
    } catch (error) {
      console.error('Edit error:', error);
      alert('Failed to update video');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;

    try {
      await api.delete(`/videos/${videoId}`);
      loadVideos();
      alert('Video deleted successfully!');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete video');
    }
  };

  const toggleCategory = (category) => {
    setUploadForm(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const extractYoutubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  return (
    <div className="tutorials-container">
      {/* Header with Upload Button */}
      <div className="tutorials-header">
        <h2>📚 Video Tutorials</h2>
        {isCoach && (
          <button
            className="upload-btn"
            onClick={() => setShowUploadForm(!showUploadForm)}
          >
            {showUploadForm ? '✕ Close' : '+ Upload Video'}
          </button>
        )}
      </div>

      {/* Upload Form */}
      {isCoach && showUploadForm && (
        <div className="upload-form-container">
          <form onSubmit={handleUploadSubmit} className="upload-form">
            <div className="form-group">
              <label>Video Title *</label>
              <input
                type="text"
                placeholder="e.g., Forehand Technique Basics"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                maxLength="200"
              />
            </div>

            <div className="form-group">
              <label>YouTube URL *</label>
              <input
                type="text"
                placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                value={uploadForm.youtubeUrl}
                onChange={(e) => setUploadForm({ ...uploadForm, youtubeUrl: e.target.value })}
              />
              {uploadForm.youtubeUrl && extractYoutubeId(uploadForm.youtubeUrl) && (
                <div className="preview-thumbnail">
                  <img
                    src={`https://img.youtube.com/vi/${extractYoutubeId(uploadForm.youtubeUrl)}/mqdefault.jpg`}
                    alt="Preview"
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Categories</label>
              <div className="categories-grid">
                {categories.map(cat => (
                  <label key={cat} className="category-checkbox">
                    <input
                      type="checkbox"
                      checked={uploadForm.categories.includes(cat)}
                      onChange={() => toggleCategory(cat)}
                    />
                    {cat}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                placeholder="Optional: Describe what this video covers"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                maxLength="1000"
                rows="3"
              />
            </div>

            <button type="submit" disabled={uploadLoading} className="submit-btn">
              {uploadLoading ? 'Uploading...' : 'Upload Video'}
            </button>
          </form>
        </div>
      )}

      {/* Search & Filter */}
      <div className="search-filter-container">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search by title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="category-filter"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Videos Grid */}
      {loading ? (
        <div className="loading">Loading videos...</div>
      ) : videos.length === 0 ? (
        <div className="no-videos">
          {searchTerm || selectedCategory ? 'No videos found' : 'No videos yet'}
        </div>
      ) : (
        <div className="videos-grid">
          {videos.map(video => (
            <div key={video._id} className="video-card">
                  {/* Video Info */}
              <div className="video-info">
                {editingId === video._id && isCoach && video.coachId._id === user._id ? (
                  <input
                    type="text"
                    value={editForm.title || video.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="edit-title-input"
                  />
                ) : (
                  <h3 className="video-title">{video.title}</h3>
                )}

                <p className="coach-name">By {video.coachId.name}</p>

                {video.categories && video.categories.length > 0 && (
                  <div className="categories-tags">
                    {video.categories.map(cat => (
                      <span key={cat} className="category-tag">{cat}</span>
                    ))}
                  </div>
                )}

                {video.description && (
                  <p className="description">{video.description}</p>
                )}

                {/* Actions */}
                {isCoach && video.coachId._id === user._id && (
                  <div className="video-actions">
                    {editingId === video._id ? (
                      <>
                        <button
                          className="save-btn"
                          onClick={() => handleEdit(video._id)}
                        >
                          Save
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="edit-btn"
                          onClick={() => {
                            setEditingId(video._id);
                            setEditForm({ title: video.title });
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(video._id)}
                        >
                          🗑️ Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* YouTube Player */}
              <div className="video-player">
                <iframe
                  width="100%"
                  height="180"
                  src={`https://www.youtube.com/embed/${video.youtubeVideoId}`}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
